import React, { useState, useEffect } from 'react';
import { DollarSign, Download } from 'lucide-react';
import api from '../config/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
// CommissionTable removed - using inline table for commissions
import toast from 'react-hot-toast';

const Commissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommissions();
    fetchStats();
  }, []);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/commissions');
      setCommissions(response.data.data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/commissions/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching commission stats:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="page-header-title">Commissions</h1>
            <p className="page-header-subtitle">Track and manage commission earnings</p>
          </div>
          <button className="btn btn-secondary mt-4 sm:mt-0 inline-flex">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      {/* Metrics Bar */}
      {stats && (
        <div className="metrics-bar">
          <div className="metrics-grid">
            <div className="metric-item">
              <span className="metric-label">This Month</span>
              <span className="metric-value">KES {stats.monthly.toLocaleString()}</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">This Quarter</span>
              <span className="metric-value">KES {stats.quarterly.toLocaleString()}</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">This Year</span>
              <span className="metric-value">KES {stats.yearly.toLocaleString()}</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Total Earned</span>
              <span className="metric-value">KES {stats.total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Commission Table */}
      <div className="border-t border-gray-200">
        {commissions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>Policy Type</th>
                  <th>Commission Amount</th>
                  <th>Commission Rate</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((commission) => (
                  <tr key={commission.id}>
                    <td className="font-medium text-gray-900">{commission.clientName}</td>
                    <td className="text-gray-600">{commission.policyType || 'Standard'}</td>
                    <td className="font-semibold text-gray-900">KES {commission.amount.toLocaleString()}</td>
                    <td className="text-gray-600">{commission.rate || 'N/A'}%</td>
                    <td className="text-gray-600">{new Date(commission.date).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-label ${commission.status === 'paid' ? 'status-active' : 'status-pending'}`}>
                        {commission.status ? commission.status.charAt(0).toUpperCase() + commission.status.slice(1) : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8">
            <div className="empty-state">
              <DollarSign className="empty-state-icon h-12 w-12" />
              <h3 className="empty-state-title">No commissions recorded</h3>
              <p className="empty-state-text">Commission data will appear here as policies are added</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Commissions;
